
import instance from "../auth/axios";
import { requestData } from "./requestUtils";

// Get all sites for the authenticated user
/**
 * Fetches all Sites for the authenticated user.
 *
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getUserSites = ({ signal } = {}) => {
  return requestData(instance.get("/sites", { signal }), {
    errorMessage: "Error fetching user sites:",
    quietCancellation: true,
  });
};

// Get a specific site by ID
/**
 * Fetches one Site by identifier.
 *
 * @param {string|number} siteId - Site identifier.
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getSiteById = (siteId, { signal } = {}) => {
  return requestData(instance.get(`/sites/${siteId}`, { signal }), {
    errorMessage: "Error fetching site:",
    quietCancellation: true,
  });
};

// Create a new site.
// Mutations are intentionally not cancelled because cancellation cannot prove
// whether the server persisted the mutation.
/**
 * Creates a Site.
 *
 * Mutation requests are intentionally not cancelled because client cancellation cannot
 * prove that the server did not persist the mutation.
 *
 * @param {Object} siteData - Site values to create.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const createSite = siteData => {
  return requestData(instance.post("/sites", siteData), { errorMessage: "Error creating site:" });
};

// Update a site.
/**
 * Updates a Site.
 *
 * @param {string|number} siteId - Site identifier.
 * @param {Object} siteData - Site fields to update.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const updateSite = (siteId, siteData) => {
  return requestData(instance.put(`/sites/${siteId}`, siteData), { errorMessage: "Error updating site:" });
};

// Delete a site.
/**
 * Deletes a Site.
 *
 * @param {string|number} siteId - Site identifier.
 * @param {number} expectedVersion - Version captured when the delete confirmation opened.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const deleteSite = (siteId, expectedVersion) => {
  return requestData(
    instance.delete(`/sites/${siteId}`, {
      data: { expected_version: expectedVersion },
    }),
    { errorMessage: "Error deleting site:" }
  );
};

// Get SiteDetails overview
/**
 * Fetches the bounded overview read model for one Site.
 *
 * @param {string|number} siteId - Site identifier.
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getSiteOverview = (siteId, { signal } = {}) => {
  return requestData(instance.get(`/sites/${siteId}/overview`, { signal }), {
    errorMessage: "Error fetching site overview:",
    quietCancellation: true,
  });
};

// Get site KPIs
/**
 * Fetches KPIs for one Site.
 *
 * @param {string|number} siteId - Site identifier.
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getSiteKpis = (siteId, { signal } = {}) => {
  return requestData(instance.get(`/sites/${siteId}/kpis`, { signal }), {
    errorMessage: "Error fetching site KPIs:",
    quietCancellation: true,
  });
};

// Get all sites KPIs
/**
 * Fetches aggregated KPIs for all Sites owned by the authenticated user.
 *
 * @param {Object} [options] - Request options.
 * @param {AbortSignal} [options.signal] - Signal used to cancel this read request.
 * @returns {Promise<Object|undefined>} API response payload.
 */
const getAllSitesKpis = ({ signal } = {}) => {
  return requestData(instance.get("/sites/kpis", { signal }), {
    errorMessage: "Error fetching all sites KPIs:",
    quietCancellation: true,
  });
};

const getRecentlyDeletedSites = ({ signal } = {}) =>
  requestData(instance.get("/sites/recently-deleted", { signal }));

const restoreSite = siteId =>
  requestData(instance.post(`/sites/${siteId}/restore`));

export {
  getUserSites,
  getSiteById,
  createSite,
  updateSite,
  deleteSite,
  getSiteOverview,
  getSiteKpis,
  getAllSitesKpis,
  getRecentlyDeletedSites,
  restoreSite,
};
